// ════════════════════════════════════════════════════════════════════════════
// LpaChart — wrapper unificado em cima de Recharts
// Sprint Tech Debt #2 (audit P1-12)
//
// Antes: cada chart re-implementava tooltip, legend, axis, formatters, cores
// (70+ usos diretos de Recharts em pages/ e admin/).
//
// Agora: <LpaChart kind="line" data={...} series={[...]} /> com defaults
// Tech-Noir + design tokens via CSS vars.
//
// Suporta: line · area · bar · pie · combo (multi-series).
// ════════════════════════════════════════════════════════════════════════════

import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  type TooltipProps,
} from "recharts";

// ─── Design tokens (resolvidos em runtime via CSS vars) ────────────────────

const TOKEN = {
  text: "var(--lpa-text)",
  muted: "var(--lpa-muted)",
  border: "var(--lpa-border)",
  card: "var(--lpa-card)",
  surface: "var(--lpa-surface)",
  font: "var(--lpa-font-sans)",
  fontMono: "var(--lpa-font-mono)",
  shadow: "var(--lpa-shadow-md)",
  radius: "var(--lpa-radius-md)",
};

// Paleta padrão (cyclical)
const DEFAULT_PALETTE = [
  "#1D4ED8", // accent
  "#0B6C3E", // emerald
  "#C8A14A", // gold
  "#0EA5E9", // sky
  "#7C3AED", // violet
  "#EF4444", // red
  "#F59E0B", // amber
  "#10B981", // green
];

// ─── Tipos públicos ─────────────────────────────────────────────────────────

export type ChartKind = "line" | "area" | "bar" | "pie" | "combo";

export interface SeriesDef {
  /** Chave em data[] (ex: "value", "cdi") */
  key: string;
  /** Nome exibido em legenda/tooltip */
  label: string;
  /** Cor (default: paleta cyclical) */
  color?: string;
  /** Para combo charts: tipo do trace */
  kind?: "line" | "area" | "bar";
  /** Formatter de valor (default: locale BRL adaptive) */
  format?: (v: number) => string;
  /** Tracejado (line/area) */
  strokeDasharray?: string;
  /** Stack id (para AreaChart/BarChart empilhados) */
  stackId?: string;
  /** Eixo Y secundário */
  yAxisId?: "left" | "right";
}

export interface RefLine {
  value: number;
  label?: string;
  color?: string;
  strokeDasharray?: string;
  axis?: "x" | "y";
}

export interface LpaChartProps {
  kind: ChartKind;
  data: Array<Record<string, unknown>>;
  /** Para line/area/bar/combo: definição de cada série a desenhar */
  series?: SeriesDef[];
  /** Para pie: chave de label (ex: "name") e key de valor */
  pieLabelKey?: string;
  pieValueKey?: string;
  /** X-axis key (default: "date" ou "name") */
  xKey?: string;
  /** Altura do container (default: 280) */
  height?: number;
  /** Mostra eixo Y? */
  showYAxis?: boolean;
  /** Mostra grade? */
  showGrid?: boolean;
  /** Mostra legenda? */
  showLegend?: boolean;
  /** Tooltip habilitado? */
  showTooltip?: boolean;
  /** Linhas de referência horizontais ou verticais */
  referenceLines?: RefLine[];
  /** Formatter custom para tick X */
  xTickFormatter?: (v: unknown) => string;
  /** Formatter default para Y (e tooltip) — pode ser overridado por série */
  yFormatter?: (v: number) => string;
  /** Loading state — mostra skeleton */
  loading?: boolean;
  /** Label vazio quando data.length === 0 */
  emptyLabel?: string;
  /** Margem interna */
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
}

// ─── Formatters default ─────────────────────────────────────────────────────

function fmtCompact(v: number): string {
  if (!Number.isFinite(v)) return "—";
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return v.toFixed(0);
}

function fmtPct(v: number): string {
  return `${v.toFixed(2)}%`;
}

// ─── Custom tooltip Tech-Noir ──────────────────────────────────────────────

type TooltipExtraProps = {
  series?: SeriesDef[];
  yFormatter?: (v: number) => string;
};

function LpaTooltip(
  props: TooltipProps<number | string, string | number> & TooltipExtraProps
): React.ReactElement | null {
  const { active, payload, label, series = [], yFormatter } = props;
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div
      style={{
        background: TOKEN.card,
        border: `1px solid ${TOKEN.border}`,
        borderRadius: TOKEN.radius,
        padding: "8px 12px",
        boxShadow: TOKEN.shadow,
        fontFamily: TOKEN.font,
        fontSize: 11,
        minWidth: 120,
      }}
    >
      {label !== undefined && (
        <div
          style={{
            fontFamily: TOKEN.fontMono,
            fontSize: 9,
            color: TOKEN.muted,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            marginBottom: 6,
          }}
        >
          {String(label)}
        </div>
      )}
      {payload.map((p, idx) => {
        const seriesDef = series.find((s) => s.key === p.dataKey);
        const fmt = seriesDef?.format ?? yFormatter ?? fmtCompact;
        const value = typeof p.value === "number" ? fmt(p.value) : String(p.value);
        return (
          <div
            key={`${p.dataKey ?? idx}`}
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 14,
              padding: "2px 0",
              fontSize: 11,
              color: TOKEN.text,
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  background: p.color,
                  display: "inline-block",
                }}
              />
              {seriesDef?.label ?? String(p.dataKey)}
            </span>
            <strong style={{ fontFamily: TOKEN.fontMono, fontWeight: 600 }}>{value}</strong>
          </div>
        );
      })}
    </div>
  );
}

// ─── Skeleton ───────────────────────────────────────────────────────────────

function ChartSkeleton({ height }: { height: number }): React.ReactElement {
  return (
    <div
      style={{
        height,
        width: "100%",
        background: TOKEN.surface,
        border: `1px solid ${TOKEN.border}`,
        borderRadius: TOKEN.radius,
        display: "grid",
        placeItems: "center",
        color: TOKEN.muted,
        fontFamily: TOKEN.fontMono,
        fontSize: 10,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
      }}
      role="status"
      aria-label="Carregando gráfico"
    >
      Carregando…
    </div>
  );
}

function ChartEmpty({ height, label }: { height: number; label: string }): React.ReactElement {
  return (
    <div
      style={{
        height,
        width: "100%",
        border: `1px dashed ${TOKEN.border}`,
        borderRadius: TOKEN.radius,
        display: "grid",
        placeItems: "center",
        color: TOKEN.muted,
        fontFamily: TOKEN.fontMono,
        fontSize: 10,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
      }}
    >
      {label}
    </div>
  );
}

// ─── Componente principal ──────────────────────────────────────────────────

export default function LpaChart({
  kind,
  data,
  series = [],
  pieLabelKey = "name",
  pieValueKey = "value",
  xKey,
  height = 280,
  showYAxis = true,
  showGrid = true,
  showLegend = true,
  showTooltip = true,
  referenceLines = [],
  xTickFormatter,
  yFormatter = fmtCompact,
  loading = false,
  emptyLabel = "Sem dados no período",
  margin = { top: 12, right: 12, left: 0, bottom: 4 },
}: LpaChartProps): React.ReactElement {
  const xKeyResolved = xKey ?? (data[0] && "date" in data[0] ? "date" : "name");

  // Atribui cores das séries (cyclical) se não definido
  const seriesWithColor = useMemo(
    () =>
      series.map((s, i) => ({
        ...s,
        color: s.color ?? DEFAULT_PALETTE[i % DEFAULT_PALETTE.length],
      })),
    [series]
  );

  if (loading) return <ChartSkeleton height={height} />;
  if (!data || data.length === 0) return <ChartEmpty height={height} label={emptyLabel} />;

  // ─── Pie ────────────────────────────────────────────────────────────────
  if (kind === "pie") {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <PieChart margin={margin}>
          <Pie
            data={data}
            dataKey={pieValueKey}
            nameKey={pieLabelKey}
            innerRadius="55%"
            outerRadius="85%"
            paddingAngle={2}
            stroke="none"
          >
            {data.map((_, idx) => (
              <Cell key={idx} fill={DEFAULT_PALETTE[idx % DEFAULT_PALETTE.length]} />
            ))}
          </Pie>
          {showTooltip && (
            <Tooltip
              content={
                ((p) => (
                  <LpaTooltip
                    {...(p as TooltipProps<number | string, string | number>)}
                    series={seriesWithColor}
                    yFormatter={yFormatter}
                  />
                )) as React.ComponentProps<typeof Tooltip>["content"]
              }
            />
          )}
          {showLegend && (
            <Legend
              wrapperStyle={{
                fontSize: 11,
                fontFamily: TOKEN.font,
                color: TOKEN.muted,
              }}
            />
          )}
        </PieChart>
      </ResponsiveContainer>
    );
  }

  // ─── Line / Area / Bar / Combo ──────────────────────────────────────────
  const ChartRoot =
    kind === "area"
      ? AreaChart
      : kind === "bar"
        ? BarChart
        : kind === "combo"
          ? LineChart // Combo usa LineChart como root, traços individuais escolhem tipo
          : LineChart;

  const usesRightAxis = seriesWithColor.some((s) => s.yAxisId === "right");

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ChartRoot data={data} margin={margin}>
        {showGrid && <CartesianGrid stroke={TOKEN.border} strokeDasharray="2 4" vertical={false} />}
        <XAxis
          dataKey={xKeyResolved}
          tick={{
            fill: TOKEN.muted,
            fontSize: 10,
            fontFamily: TOKEN.fontMono,
          }}
          tickLine={false}
          axisLine={{ stroke: TOKEN.border }}
          tickFormatter={xTickFormatter ? (v: unknown) => xTickFormatter(v) : undefined}
        />
        {showYAxis && (
          <YAxis
            yAxisId="left"
            tick={{
              fill: TOKEN.muted,
              fontSize: 10,
              fontFamily: TOKEN.fontMono,
            }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => (typeof v === "number" ? yFormatter(v) : String(v))}
          />
        )}
        {usesRightAxis && (
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{
              fill: TOKEN.muted,
              fontSize: 10,
              fontFamily: TOKEN.fontMono,
            }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => (typeof v === "number" ? yFormatter(v) : String(v))}
          />
        )}

        {showTooltip && (
          <Tooltip
            content={
              ((p) => (
                <LpaTooltip
                  {...(p as TooltipProps<number | string, string | number>)}
                  series={seriesWithColor}
                  yFormatter={yFormatter}
                />
              )) as React.ComponentProps<typeof Tooltip>["content"]
            }
            cursor={{ stroke: TOKEN.border, strokeDasharray: "2 4" }}
          />
        )}

        {showLegend && (
          <Legend
            wrapperStyle={{
              fontSize: 11,
              fontFamily: TOKEN.font,
              color: TOKEN.muted,
              paddingTop: 8,
            }}
            iconType="rect"
            iconSize={10}
          />
        )}

        {/* Reference lines */}
        {referenceLines.map((ref, i) => (
          <ReferenceLine
            key={i}
            yAxisId={ref.axis === "x" ? undefined : "left"}
            y={ref.axis === "y" || !ref.axis ? ref.value : undefined}
            x={ref.axis === "x" ? ref.value : undefined}
            stroke={ref.color ?? TOKEN.muted}
            strokeDasharray={ref.strokeDasharray ?? "4 4"}
            label={
              ref.label
                ? {
                    value: ref.label,
                    fill: TOKEN.muted,
                    fontSize: 9,
                    fontFamily: TOKEN.fontMono,
                  }
                : undefined
            }
          />
        ))}

        {/* Series traces */}
        {seriesWithColor.map((s) => {
          const traceKind = s.kind ?? (kind === "combo" ? "line" : kind);
          const yAxis = s.yAxisId ?? "left";

          if (traceKind === "area") {
            return (
              <Area
                key={s.key}
                yAxisId={yAxis}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={s.color}
                fill={s.color}
                fillOpacity={0.18}
                strokeWidth={2}
                strokeDasharray={s.strokeDasharray}
                stackId={s.stackId}
                isAnimationActive={false}
              />
            );
          }

          if (traceKind === "bar") {
            return (
              <Bar
                key={s.key}
                yAxisId={yAxis}
                dataKey={s.key}
                name={s.label}
                fill={s.color}
                stackId={s.stackId}
                isAnimationActive={false}
              />
            );
          }

          // line (default)
          return (
            <Line
              key={s.key}
              yAxisId={yAxis}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stroke={s.color}
              strokeWidth={2}
              strokeDasharray={s.strokeDasharray}
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          );
        })}
      </ChartRoot>
    </ResponsiveContainer>
  );
}

// ─── Helpers exportados ────────────────────────────────────────────────────

export const formatters = {
  compact: fmtCompact,
  pct: fmtPct,
  brl: (v: number) =>
    v.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
      maximumFractionDigits: 0,
    }),
  ppts: (v: number) => `${v >= 0 ? "+" : ""}${v.toFixed(1)}pp`,
  date: (v: unknown) => {
    if (typeof v === "string") return v;
    return String(v);
  },
};

export { LpaTooltip, DEFAULT_PALETTE };
